/**
 * Нарахування ЗП — список (канон Норми #29): InfiniteDocGrid, серверні фільтри
 * орг/вид/період нарахування (поля періоду порожні → період шапки, ч.775),
 * сортування, «×» видалення непроведеного (ч.774).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { payrolls, type PayrollListItem } from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import PeriodFilter, { useHrPeriodFilter, MONTHS_UK } from '@/shared/components/PeriodFilter';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const KIND_LABEL: Record<string, string> = { MAIN: 'Основна', ADVANCE: 'Аванс', CIVIL: 'ЦПУ' };

export default function PayrollsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const [kind, setKind] = useState<'' | 'MAIN' | 'ADVANCE' | 'CIVIL'>('');
  const per = useHrPeriodFilter();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<PayrollListItem>(
    (limit, offset, sort) => payrolls.list({
      organization_id: orgId || undefined,
      payroll_kind: kind || undefined,
      ...per.params,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, kind, ...per.key],
    { attachmentsType: 'doc.payrolls' },
  );
  const sc = useSignControlInfinite<PayrollListItem>('doc.payrolls', orgId);

  const cols = useMemo<ColDef<PayrollListItem>[]>(() => [
    { field: 'posted', headerName: '', width: 40 },
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: PayrollListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 140 },
    {
      field: 'organization_id', headerName: 'Організація', width: 190,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      colId: 'period', headerName: 'Період', width: 130,
      valueGetter: (p) => p.data?.period_month
        ? `${MONTHS_UK[p.data.period_month]} ${p.data.period_year}` : (p.data ? '—' : ''),
    },
    {
      field: 'payroll_kind', headerName: 'Вид', width: 110,
      cellRenderer: (p: { data?: PayrollListItem; value?: string }) => {
        if (!p.data) return '';
        const k = p.value ?? 'MAIN';
        const adv = k === 'ADVANCE';
        return (
          <span style={{
            background: adv ? '#dbeafe' : '#dcfce7', color: adv ? '#1e40af' : '#166534',
            padding: '2px 8px', borderRadius: 4, fontSize: '0.85em',
          }}>{KIND_LABEL[k] ?? k}</span>
        );
      },
    },
    {
      field: 'total_gross', headerName: 'Брутто', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'total_net', headerName: 'До виплати', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Нарахування ЗП<TitleTotal total={total} /></h2>
        <button className="primary" onClick={() => navigate('/payrolls/new')}>
          + Нове нарахування
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value as '' | 'MAIN' | 'ADVANCE' | 'CIVIL')} style={{ width: 150 }}>
          <option value="">усі види</option>
          <option value="MAIN">Основна</option>
          <option value="ADVANCE">Аванс</option>
          <option value="CIVIL">ЦПУ</option>
        </select>
        <PeriodFilter {...per.bind} />
      </div>
      <InfiniteDocGrid<PayrollListItem>
        deleteRow={{ remove: (id) => payrolls.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="payrolls"
        onRowDoubleClicked={(r) => navigate(`/payrolls/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
